import React, { useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, LineChart, Line, Area, AreaChart, Cell } from 'recharts';
import { ArrowRight, TrendingUp, AlertTriangle, Check, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

const StationDetailedAnalysis = ({ station }) => {
  const [activeTab, setActiveTab] = useState('potential');
  
  if (!station) {
    return (
      <div className="bg-white p-8 rounded-lg shadow text-center">
        <p className="text-gray-500">Please select a station on the map to view detailed analysis</p>
      </div>
    );
  }

  const potentialGrowth = (Math.random() * 4 + 2).toFixed(1);
  
  const currentVolume = station.volume || 0;
  const potentialVolume = Math.round(currentVolume * (1 + potentialGrowth / 100));
  
  const priceSensitivityData = [
    { price: '-0.75', volume: Math.round(currentVolume * 1.12) },
    { price: '-0.50', volume: Math.round(currentVolume * 1.08) },
    { price: '-0.25', volume: Math.round(currentVolume * 1.04) },
    { price: 'Current', volume: currentVolume },
    { price: '+0.25', volume: Math.round(currentVolume * 0.96) },
    { price: '+0.50', volume: Math.round(currentVolume * 0.92) },
    { price: '+0.75', volume: Math.round(currentVolume * 0.88) },
  ];
  
  const daypartData = [
    { name: '6-9 AM', current: Math.round(currentVolume * 0.25), potential: Math.round(currentVolume * 0.25 * (1 + potentialGrowth / 100)) },
    { name: '9-12 PM', current: Math.round(currentVolume * 0.20), potential: Math.round(currentVolume * 0.20 * (1 + potentialGrowth / 100)) },
    { name: '12-3 PM', current: Math.round(currentVolume * 0.15), potential: Math.round(currentVolume * 0.15 * (1 + potentialGrowth / 100)) },
    { name: '3-6 PM', current: Math.round(currentVolume * 0.25), potential: Math.round(currentVolume * 0.25 * (1 + potentialGrowth / 100)) },
    { name: '6-9 PM', current: Math.round(currentVolume * 0.10), potential: Math.round(currentVolume * 0.10 * (1 + potentialGrowth / 100)) },
    { name: '9-12 AM', current: Math.round(currentVolume * 0.05), potential: Math.round(currentVolume * 0.05 * (1 + potentialGrowth / 100)) },
  ];
  
  const marketShareData = [
    { name: 'Your Brand', value: station.marketShare || 25 },
    { name: 'Competitor A', value: Math.round(Math.random() * 15) + 10 },
    { name: 'Competitor B', value: Math.round(Math.random() * 15) + 10 },
    { name: 'Competitor C', value: Math.round(Math.random() * 10) + 5 },
    { name: 'Others', value: 100 - ((station.marketShare || 25) + Math.round(Math.random() * 15) + 10 + Math.round(Math.random() * 15) + 10 + Math.round(Math.random() * 10) + 5) }
  ];
  
  const COLORS = ['#8B5CF6', '#3B82F6', '#F59E0B', '#10B981', '#A1A1AA'];
  
  const insights = [
    { category: 'Pricing', status: 'attention', text: 'Current pricing may be suboptimal based on elasticity analysis. Consider -0.25 THB adjustment.' },
    { category: 'Operations', status: 'positive', text: 'Extended opening hours could capture 3.2% additional volume in the 9PM-12AM period.' },
    { category: 'Competition', status: 'neutral', text: 'All retail sites with newly opened regional-minor competitor in close proximity (>0.5km) are losing fuel volume compared to the rest of the network. Recommend action is a developing targeted loyalty promotion program with higher points per litre' },
    { category: 'Demographics', status: 'positive', text: 'Growing residential development 1.5km south presents opportunity for premium fuel mix.' },
  ];
  
  const handleGenerateReport = () => {
    toast.success('Detailed report is being generated and will be emailed to you.');
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Station Detailed Analysis: {station.name}</h3>
        </div>
        
        <div className="p-6">
          <div className="flex border-b border-gray-200">
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'potential' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('potential')}
            >
              Market Potential
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'pricing' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('pricing')}
            >
              Price Elasticity
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'daypart' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('daypart')}
            >
              Daypart Analysis
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'competition' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('competition')}
            >
              Competition
            </button>
          </div>
          
          <div className="mt-6">
            {activeTab === 'potential' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                    <h4 className="text-sm font-medium text-purple-800 mb-1">Current Volume</h4>
                    <div className="text-2xl font-medium text-purple-900">{currentVolume.toLocaleString()} L/day</div>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                    <h4 className="text-sm font-medium text-green-800 mb-1">Market Potential Growth</h4>
                    <div className="text-2xl font-medium text-green-900">+{potentialGrowth}%</div>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <h4 className="text-sm font-medium text-blue-800 mb-1">Potential Volume</h4>
                    <div className="text-2xl font-medium text-blue-900">{potentialVolume.toLocaleString()} L/day</div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
                  <h4 className="text-md font-medium text-gray-800 mb-4">Growth Drivers</h4>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                      <div className="text-sm flex-1">Optimize pricing strategy</div>
                      <div className="text-sm font-medium text-green-800">+{(potentialGrowth * 0.4).toFixed(1)}%</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                      <div className="text-sm flex-1">Enhanced loyalty program</div>
                      <div className="text-sm font-medium text-blue-800">+{(potentialGrowth * 0.3).toFixed(1)}%</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-purple-500 rounded-full mr-2"></div>
                      <div className="text-sm flex-1">Expanded cafe offerings</div>
                      <div className="text-sm font-medium text-purple-800">+{(potentialGrowth * 0.2).toFixed(1)}%</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-amber-500 rounded-full mr-2"></div>
                      <div className="text-sm flex-1">Improved site visibility</div>
                      <div className="text-sm font-medium text-amber-800">+{(potentialGrowth * 0.1).toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-800 mb-4">Market Opportunity</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Based on trade area analysis, demographic trends, and competitive intelligence, this station has significant growth potential. Key opportunities include:
                  </p>
                  <ul className="list-disc pl-5 text-sm text-gray-600 space-y-2 mb-4">
                    <li>Optimize fuel pricing strategy based on customer price sensitivity</li>
                    <li>Enhanced loyalty program targeting commuter segment</li>
                    <li>Expansion of cafe offerings to capture breakfast and lunch crowds</li>
                    <li>Improved site visibility and signage to attract highway traffic</li>
                  </ul>
                  <p className="text-sm text-gray-600">
                    Implementing these strategies could result in a <span className="font-medium text-green-700">{potentialGrowth}%</span> volume increase within 6-12 months.
                  </p>
                </div>
              </div>
            )}
            
            {activeTab === 'pricing' && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-4">Price Elasticity Analysis</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={priceSensitivityData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis dataKey="price" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${value.toLocaleString()} L/day`, 'Volume']} />
                      <Legend />
                      <Bar dataKey="volume" fill="#8B5CF6" name="Daily Volume" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h5 className="font-medium text-gray-800 mb-2">Pricing Recommendations</h5>
                  <p className="text-sm text-gray-600">
                    Analysis indicates high price sensitivity in this market. A price reduction of 0.25 THB could potentially increase volume by up to 4%, resulting in a net revenue increase of 2.2%.
                  </p>
                </div>
              </div>
            )}
            
            {activeTab === 'daypart' && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-4">Daypart Performance Analysis</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={daypartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${value.toLocaleString()} L`, 'Volume']} />
                      <Legend />
                      <Bar dataKey="current" fill="#3B82F6" name="Current Volume" />
                      <Bar dataKey="potential" fill="#8B5CF6" name="Potential Volume" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h5 className="font-medium text-gray-800 mb-2">Daypart Opportunity</h5>
                  <p className="text-sm text-gray-600">
                    Morning (6-9 AM) and evening (3-6 PM) commute periods represent strongest performance. Late evening (9 PM-12 AM) shows greatest opportunity for growth through extended cafe hours and targeted promotions.
                  </p>
                </div>
              </div>
            )}
            
            {activeTab === 'competition' && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-4">Competitive Landscape</h4>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={marketShareData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip formatter={(value) => [`${value}%`, 'Market Share']} />
                      <Bar dataKey="value" fill="#8B5CF6" name="Market Share">
                        {marketShareData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h5 className="font-medium text-gray-800 mb-2">Competitive Intelligence</h5>
                  <p className="text-sm text-gray-600">
                    Your station maintains a {station.marketShare || 25}% market share with primary competition from Competitor A. Opportunity exists to gain share through enhanced premium fuel offerings and loyalty promotions.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Key Insights</h3>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            {insights.map((insight, index) => (
              <div key={index} className={`p-3 rounded-lg ${
                insight.status === 'positive' ? 'bg-green-50 border border-green-100' :
                insight.status === 'attention' ? 'bg-amber-50 border border-amber-100' :
                'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-start">
                  <div className="mr-3 mt-0.5">
                    {insight.status === 'positive' ? 
                      <Check size={16} className="text-green-600" /> :
                      insight.status === 'attention' ?
                      <AlertTriangle size={16} className="text-amber-600" /> :
                      <TrendingUp size={16} className="text-gray-600" />
                    }
                  </div>
                  <div>
                    <h5 className={`text-sm font-medium ${
                      insight.status === 'positive' ? 'text-green-800' :
                      insight.status === 'attention' ? 'text-amber-800' :
                      'text-gray-800'
                    }`}>
                      {insight.category}
                    </h5>
                    <p className="text-sm text-gray-600 mt-1">{insight.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6">
            <button 
              className="w-full px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 flex items-center justify-center"
              onClick={handleGenerateReport}
            >
              Generate Detailed Report <ExternalLink size={14} className="ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StationDetailedAnalysis;
